import { useEffect, useMemo, useState } from "react";
import { useData } from "vike-react/useData";
import VariantPicker from "@/components/shop/VariantPicker";
import AddToCartButton from "@/components/shop/AddToCartButton";
import ShopCartPill from "@/components/shop/ShopCartPill";
import ProductDetailTuner, { PRODUCT_TUNER_DEFAULTS, type ProductTunerState } from "@/components/shop/ProductDetailTuner";
import { resolveVariant, defaultSelection } from "@/lib/variant-select";
import type { ProductData } from "./+data";
import styles from "./product.module.css";

export default function ProductDetailPage() {
  const { product } = useData<ProductData>();
  const [selected, setSelected] = useState<number[]>(() => defaultSelection(product));
  const [tuner, setTuner] = useState<ProductTunerState>(PRODUCT_TUNER_DEFAULTS);
  const [activeImage, setActiveImage] = useState(0);

  const variant = useMemo(() => resolveVariant(product.variants, selected), [product.variants, selected]);

  // Show the mockups that depict the chosen variant (i.e. the selected color).
  // Printify tags each mockup with the variant ids it depicts; filtering by the
  // resolved variant narrows a 25-image dump down to that color's shots. Falls
  // back to the full gallery when nothing matches (or on older cached data).
  const visibleImages = useMemo(() => {
    const all = product.gallery ?? [];
    if (all.length === 0) {
      return product.images.map((src) => ({ src, variantIds: [], isFront: false }));
    }
    if (!variant) return all;
    const matched = all.filter((g) => g.variantIds.includes(variant.id));
    return matched.length ? matched : all;
  }, [product.gallery, product.images, variant]);

  // Reset to the first (front) shot whenever the depicted variant changes.
  useEffect(() => {
    setActiveImage(0);
  }, [variant?.id]);

  function onPick(optionIndex: number, valueId: number) {
    setSelected((prev) => prev.map((v, i) => (i === optionIndex ? valueId : v)));
  }

  const heroSrc = visibleImages[activeImage]?.src ?? visibleImages[0]?.src ?? product.frontMockup;
  const price = variant ? `$${variant.price.toFixed(2)}` : `$${product.priceMin.toFixed(2)} – $${product.priceMax.toFixed(2)}`;
  const gridStyle = {
    "--gallery-radius": `${tuner.galleryRadius}px`,
    "--panel-gap": `${tuner.panelGap}px`,
    "--hero-max": `${tuner.heroMax}px`,
    "--swatch-size": `${tuner.swatchSize}px`,
  } as React.CSSProperties;

  return (
    <main className={styles.page} style={gridStyle}>
      <a href="/shop" className={styles.back}>← Shop</a>
      <div className={styles.layout}>
        <section className={styles.gallery}>
          <img className={styles.hero} src={heroSrc} alt={product.title} width={1000} height={1000} />
          {visibleImages.length > 1 && (
            <div className={styles.thumbs}>
              {visibleImages.map((img, i) => (
                <button key={img.src} className={`${styles.thumb} ${i === activeImage ? styles.thumbActive : ""}`} onClick={() => setActiveImage(i)} aria-label={`View image ${i + 1}`}>
                  <img src={img.src} alt="" width={120} height={120} loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </section>

        <section className={styles.info}>
          <h1 className={styles.title}>{product.title}</h1>
          <div className={styles.price}>{price}</div>
          <VariantPicker options={product.options} selected={selected} onChange={onPick} />
          <AddToCartButton product={product} variant={variant} />
          {product.description && <div className={styles.desc}>{product.description}</div>}
        </section>
      </div>

      <ShopCartPill />
      <ProductDetailTuner state={tuner} setState={setTuner} />
    </main>
  );
}
